import { createContext, useCallback, useContext, useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

/**
 * First-run onboarding status, fetched once per page load (issue #289).
 *
 * WHY A CONTEXT AND NOT A HOOK PER CONSUMER. Three places need this answer —
 * the gate that redirects an unfinished player to the walkthrough, the
 * walkthrough itself, and the Dashboard's resume prompt — and they are
 * mounted at the same time. A hook would make that three requests per page
 * load for one fact. The provider sits on the protected layout route, so it
 * mounts once when the app loads and survives every in-app navigation
 * underneath it: navigating dashboard -> development -> profile costs no
 * further requests.
 *
 * PLAYER-ONLY, STRUCTURALLY. A coach or admin has no player profile, so
 * `GET /players/me/onboarding` 404s for them by construction. Rather than
 * making that request and discarding the answer, the provider never makes it
 * at all for a non-player and reports "nothing to do". That is slice 4 of
 * the ticket — "don't hand this flow to a coach" — held at the cheapest
 * possible layer: no request, no state, no gate.
 *
 * IT FAILS OPEN, ON PURPOSE. If the request fails, `status` stays null and
 * `required` reads false, so the app renders exactly as it did before this
 * feature existed. The two failure directions are not symmetric: failing
 * open costs a player one session in which they were not offered a
 * walkthrough they can reach any time from the Dashboard; failing closed
 * would hold every player in a first-run wrapper because one request
 * flaked. #271's rule stated for a router instead of a panel — never let a
 * failed read become an assertion about the player.
 */
const OnboardingCtx = createContext(null);

const EMPTY = Object.freeze({
  status: null,
  required: false,
  step: null,
  showResumePrompt: false,
  loading: false,
  refresh: async () => {},
  applyStatus: () => {},
});

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  // `user` is already resolved here — the provider is mounted inside
  // `ProtectedRoute`, which renders its spinner until /auth/me answers.
  const isPlayer = user?.role === "player";
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(isPlayer);

  const refresh = useCallback(async () => {
    if (!isPlayer) {
      setStatus(null);
      setLoading(false);
      return null;
    }
    setLoading(true);
    try {
      const { data } = await api.get("/players/me/onboarding");
      setStatus(data);
      return data;
    } catch (e) {
      // See the header: a failed read is not evidence that this player needs
      // onboarding, and it is certainly not grounds for holding them in it.
      console.error("OnboardingProvider: failed to read onboarding status", e);
      setStatus(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isPlayer]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  /** Push a status the server just returned (from the finish/skip POST)
   * straight into context — the gate must stop redirecting in the same tick
   * the player clicks Skip, not after a second round trip. Same shape as
   * AuthContext's `updateUser`, and for the same reason. */
  const applyStatus = useCallback((next) => setStatus(next || null), []);

  if (!isPlayer) {
    return <OnboardingCtx.Provider value={EMPTY}>{children}</OnboardingCtx.Provider>;
  }

  return (
    <OnboardingCtx.Provider
      value={{
        status,
        required: !!status?.required,
        step: status?.step || null,
        showResumePrompt: !!status?.show_resume_prompt,
        loading,
        refresh,
        applyStatus,
      }}
    >
      {children}
    </OnboardingCtx.Provider>
  );
}

/** Safe outside the provider (returns the inert value), so a page that is
 * rendered in isolation — a test, or a future route outside the protected
 * layout — never crashes on a missing provider. */
export const useOnboarding = () => useContext(OnboardingCtx) || EMPTY;
